import { useState, useCallback, useEffect } from 'react';

import Storage, { StorageKeys } from 'lane-shared/helpers/Storage';

/**
 * Custom hook to manage Building Engines Prism manual login flag state.
 * This hook handles the stateful management of whether the user is using
 * manual login vs SSO login for Building Engines Prism integration.
 */
export function useBEPrismManualLogin() {
  const [loading, setLoading] = useState(true); // Start with loading=true
  const [isManualLogin, setIsManualLogin] = useState(false);

  /**
   * Sets the manual login flag in storage and updates local state
   */
  const setManualLoginFlag = useCallback(async (isManual: boolean): Promise<void> => {
    setLoading(true);

    try {
      await Storage.setItem(StorageKeys.BE_PRISM_MANUAL_LOGIN, isManual);
      setIsManualLogin(isManual);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clears the manual login flag from storage and resets local state
   */
  const clearManualLoginFlag = useCallback(async (): Promise<void> => {
    setLoading(true);

    try {
      await Storage.removeItem(StorageKeys.BE_PRISM_MANUAL_LOGIN);
      setIsManualLogin(false);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Initialize the hook by loading the current flag state from storage
   * and subscribe to changes from other parts of the application
   */
  useEffect(() => {
    const loadInitialState = async () => {
      try {
        const flag = await Storage.getItemMaybe(StorageKeys.BE_PRISM_MANUAL_LOGIN);

        setIsManualLogin(Boolean(flag));
      } finally {
        setLoading(false);
      }
    };

    // Subscribe to storage changes to keep hook in sync across multiple instances
    const unsubscribe = Storage.subscribe(StorageKeys.BE_PRISM_MANUAL_LOGIN, async () => {
      const flag = await Storage.getItemMaybe(StorageKeys.BE_PRISM_MANUAL_LOGIN);

      setIsManualLogin(Boolean(flag));
    });

    loadInitialState();

    return unsubscribe;
  }, []);

  return {
    loading,
    isManualLogin,
    setManualLoginFlag,
    clearManualLoginFlag,
  };
}
